import { Button, Center, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import { extractErrorMessage } from "@equidefi/portals";
import { AttemptsWarning } from "@equidefi/portals/components/AttemptsWarning";
import { SystemDownBanner } from "@equidefi/portals/components/SystemDownBanner";
import { useLogin } from "@equidefi/portals/hooks/useAuthentication";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useLogoutReason } from "@equidefi/portals/hooks/useLogoutReason";
import { BannerAlert, Input, Password, Text } from "@equidefi/ui";
import LoginContainer from "./LoginContainer";

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Please enter a valid email").required("Email is required."),
  password: Yup.string().required("Password is required."),
});

const initialValues = {
  email: "",
  password: "",
};

const Login = (props) => {
  useDocumentTitle("Investor Sign In");
  const navigate = useNavigate();
  const toast = useToast();
  const [spinner, showSpinner] = useState(false);
  const location = useLocation();
  const login = useLogin();
  const { logoutReason, logoutMessage } = useLogoutReason({ consumeReason: true });

  const handleLogin = async (form) => {
    showSpinner(true);

    try {
      const email = form.email.toLowerCase().trim();

      await login.mutateAsync({
        email,
        password: form.password,
        type: "investor",
      });

      navigate("/mfa", {
        state: { email, password: form.password, ...location?.state },
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(error, "Incorrect email or password, please try again");
      toast({
        status: "error",
        description: errorMessage,
      });
    } finally {
      showSpinner(false);
    }
  };

  const { handleSubmit, errors, touched, getFieldProps, isValid } = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: handleLogin,
    validateOnMount: true,
  });

  return (
    <LoginContainer>
      <SystemDownBanner />
      {logoutReason && (
        <BannerAlert status="warning" titleLayout="stacked" hideIcon={false} mb="6" p={4} title={logoutMessage} />
      )}
      <AttemptsWarning error={login.error} mb="6" />

      <form style={{ width: "100%" }} onSubmit={handleSubmit}>
        <Input
          error={errors.email}
          isInvalid={errors.email && touched.email}
          placeholder="Email Address"
          autoFocus
          {...getFieldProps("email")}
          mb="5"
          h="12"
        />
        <Password
          error={errors.password}
          isInvalid={errors.password && touched.password}
          placeholder="Password"
          borderRadius={5}
          color="white"
          {...getFieldProps("password")}
          h="12"
        />
        <Button isLoading={spinner} isDisabled={!isValid} type="submit" h="12" w="full">
          Continue
        </Button>

        <Center mt="5">
          <Text
            as={Link}
            _hover={{ color: "gray.500" }}
            color="gray.400"
            fontSize="sm"
            to={`/forgot`}
            textAlign="center">
            Forgot password?
          </Text>
        </Center>
      </form>
    </LoginContainer>
  );
};

export default Login;
